import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { websocketService } from '../services/websocketService';
import { showToast } from '../components/Toast';
import MachineBottomNav from '../components/MachineBottomNav';
import '../components/MachineBottomNav.css';
import { MaintenanceRow, CollapsibleSection, MaintenanceAssignSheet } from '../components/MaintenanceUi';
import '../components/MaintenanceUi.css';
import './MachineMaintenanceList.css';
import { DSIcon, searchFieldProps } from '../design-system/primitives';

// The single Machine Maintenance landing screen (docs/MACHINE_MAINTENANCE_REQUIREMENTS.md
// §6/§8/§9). Formerly two near-identical pages - a member list (Screen 1) and
// an Organizer Dashboard (Screen 3) - that hit the same endpoint, rendered the
// same MaintenanceRow off the same payload and differed only in how they
// bucketed it. Merged 2026-08-17 into the Dashboard's layout, with the three
// genuinely organizer-only affordances (assign, checklist template, interval
// edit) gated on isOrganizer, matching how TaskList/TaskDetail already handle
// role variance in this codebase.

const RECORD_NAV_STATE = {
  state: { returnTo: '/machines/maintenance', returnLabel: 'Back to Maintenance' }
};

function MachineMaintenanceList() {
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const isOrganizer = isAdmin || (currentUser?.featureRoles || []).includes('machine_maintenance');
  const viewerId = currentUser?.id;

  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);
  const [intervalDays, setIntervalDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');

  const [assigningMachine, setAssigningMachine] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const [showIntervalEdit, setShowIntervalEdit] = useState(false);
  const [tempInterval, setTempInterval] = useState('30');
  const [savingInterval, setSavingInterval] = useState(false);

  // `silent` skips the full-page loading state - used for the live-update
  // refresh below so the list doesn't flash away every time anyone else
  // makes progress during a busy maintenance day.
  const loadAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      // /assignable-users is requireFeatureRole-gated and 403s for ordinary
      // members, so it can't join an unconditional Promise.all here - that
      // would fail the whole load for the majority of this page's viewers.
      const needsMaintenanceRes = await apiService.getNeedsMaintenance();
      setMachines(needsMaintenanceRes.data.machines || []);
      setIntervalDays(needsMaintenanceRes.data.intervalDays || 30);

      if (isOrganizer) {
        const usersRes = await apiService.getMaintenanceAssignableUsers();
        setUsers(usersRes.data || []);
      }
    } catch (err) {
      if (!silent) setError('Failed to load the maintenance list.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isOrganizer]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Many simultaneous users during a maintenance event means these buckets and
  // counts go stale fast without a live refresh.
  useEffect(() => {
    const unsubscribe = websocketService.on('maintenance_record_changed', () => {
      loadAll({ silent: true });
    });
    return unsubscribe;
  }, [loadAll]);

  const pending = useMemo(() => machines.filter((m) => m.needs_work), [machines]);
  const complete = useMemo(() => machines.filter((m) => !m.needs_work), [machines]);
  // "In Progress" = an open checklist that's already assigned or self-claimed;
  // "Needs Maintenance" is narrowed to the unclaimed leftovers so a machine
  // doesn't show up in both buckets at once.
  const unassigned = useMemo(
    () => pending.filter((m) => !m.assigned_to && !m.self_claimed_by),
    [pending]
  );
  const inProgress = useMemo(
    () => pending.filter((m) => m.assigned_to || m.self_claimed_by),
    [pending]
  );
  // Deliberately duplicated into the buckets below rather than removed from
  // them - this is a shortcut to your own work, not a separate bucket, and the
  // old member list behaved the same way.
  const myRecords = useMemo(
    () => pending.filter((m) => m.assigned_to === viewerId || m.self_claimed_by === viewerId),
    [pending, viewerId]
  );

  // The buckets below are ordered worst-off-first by the server (§10) and
  // deliberately not alphabetically, which makes hunting for one specific
  // machine by name hard once the floor has ~40 of them. Searching swaps the
  // whole page over to a single flat, A-Z result list - when you already know
  // which machine you want, "how overdue is it" is not the axis you're
  // scanning on. Matches on the assignee/claimer name too, so an Organizer can
  // also answer "what did I hand to Dave?".
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return machines
      .filter((m) => (
        (m.machine_name || '').toLowerCase().includes(q)
        || (m.assigned_to_name || '').toLowerCase().includes(q)
        || (m.self_claimed_by_name || '').toLowerCase().includes(q)
      ))
      .sort((a, b) => (a.machine_name || '').localeCompare(b.machine_name || ''));
  }, [machines, search]);

  const openRecord = (machine) => navigate(`/machines/maintenance/${machine.machine_id}`, RECORD_NAV_STATE);

  const assignAction = (machine) => {
    if (!isOrganizer) return null;
    const assigned = !!machine.assigned_to;
    return (
      <button
        type="button"
        className={`ml-assign-btn ${assigned ? 'ml-assign-btn--assigned' : ''}`}
        onClick={() => setAssigningMachine(machine)}
      >
        {assigned ? 'Reassign' : 'Assign'}
      </button>
    );
  };

  // `withAssign` is off for the Complete This Cycle bucket - assigning a
  // machine whose record is already finished for the interval isn't a real
  // action, and the button would just add noise to the longest list.
  const rows = (list, withAssign = true) => list.map((m) => (
    <MaintenanceRow
      key={m.machine_id}
      machine={m}
      viewerId={viewerId}
      onPress={openRecord}
      rightAction={withAssign ? assignAction(m) : null}
    />
  ));

  const handleAssign = async (userId) => {
    if (!assigningMachine) return;
    setAssigning(true);
    try {
      await apiService.assignMaintenanceRecord(assigningMachine.machine_id, userId);
      showToast(userId ? 'Assignment updated.' : 'Assignment removed.', 'success');
      setAssigningMachine(null);
      loadAll();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update assignment.', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleSaveInterval = async () => {
    const value = parseInt(tempInterval, 10);
    if (Number.isNaN(value) || value < 1) {
      showToast('Enter a valid number of days.', 'error');
      return;
    }
    setSavingInterval(true);
    try {
      await apiService.updateMaintenanceConfig(value);
      setIntervalDays(value);
      setShowIntervalEdit(false);
      showToast('Maintenance interval updated.', 'success');
      loadAll();
    } catch (err) {
      showToast('Failed to update the interval.', 'error');
    } finally {
      setSavingInterval(false);
    }
  };

  return (
    <div className="maintenance-list maintenance-ui bn-page-padding">
      <div className="ml-header">
        <div className="ml-header__top">
          <div>
            <h1 className="ml-header__title">Maintenance</h1>
            <p className="ml-header__subtitle">
              {pending.length} machine{pending.length !== 1 ? 's' : ''} pending
            </p>
          </div>
          <div className="ml-header__actions">
            {isOrganizer ? (
              <>
                <button type="button" className="ml-header-btn" onClick={() => navigate('/machines/maintenance/template')}>
                  Checklist
                  <span className="ml-header-btn__chevron">›</span>
                </button>
                <button
                  type="button"
                  className="ml-interval-btn"
                  onClick={() => { setTempInterval(String(intervalDays)); setShowIntervalEdit(true); }}
                >
                  <span className="ml-interval-btn__value">{intervalDays}d</span>
                  <span className="ml-interval-btn__label">
                    <span>interval</span>
                    <svg className="ml-interval-btn__edit-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </span>
                </button>
              </>
            ) : (
              // Members can't edit the cycle length but still need to know it
              // to read "overdue" on a row, so it stays visible as a static
              // chip in the same slot the Organizer's edit button occupies.
              <span className="ml-interval-chip">{intervalDays}d cycle</span>
            )}
          </div>
        </div>

        <div className="ml-search">
          <span className="ml-search__icon" aria-hidden="true">⌕</span>
          <input
            {...searchFieldProps}
            className="ml-search__input"
            placeholder="Search machines or people..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search machines"
          />
          {search && (
            <button type="button" className="ml-search__clear" onClick={() => setSearch('')} aria-label="Clear search">
              ✕
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!loading && (
        <>
          <div className="ml-summary">
            <div className="ml-summary__card ml-summary__card--pending">
              <span className="ml-summary__value">{pending.length}</span>
              <span className="ml-summary__label">Needs Work</span>
            </div>
            {isOrganizer ? (
              <div className="ml-summary__card ml-summary__card--unassigned">
                <span className="ml-summary__value">{unassigned.length}</span>
                <span className="ml-summary__label">Unassigned</span>
              </div>
            ) : (
              // "Unassigned" is an Organizer's triage concern. The equivalent
              // at-a-glance number for a member is how much of it is theirs.
              <div className="ml-summary__card ml-summary__card--mine">
                <span className="ml-summary__value">{myRecords.length}</span>
                <span className="ml-summary__label">Yours</span>
              </div>
            )}
            <div className="ml-summary__card ml-summary__card--complete">
              <span className="ml-summary__value">{complete.length}</span>
              <span className="ml-summary__label">Complete</span>
            </div>
          </div>

          {searchResults ? (
            <>
              {searchResults.length === 0 ? (
                <p className="ml-no-results">No machines or people match "{search.trim()}".</p>
              ) : (
                <>
                  <p className="ml-section-heading">Results ({searchResults.length})</p>
                  <div className="ml-row-list">{rows(searchResults)}</div>
                </>
              )}
            </>
          ) : (
            <>
              {myRecords.length > 0 && (
                <>
                  <p className="ml-section-heading ml-section-heading--mine">Your Active Records</p>
                  <div className="ml-row-list">{rows(myRecords)}</div>
                </>
              )}

              {pending.length === 0 && (
                <div className="ml-empty">
                  <div className="ml-empty__icon"><DSIcon name="check" /></div>
                  <p className="ml-empty__title">All machines are up to date</p>
                  <p className="ml-empty__body">
                    Every machine has been serviced this cycle. Nothing left to do, great work.
                  </p>
                </div>
              )}

              {unassigned.length > 0 && (
                <CollapsibleSection title="Needs Maintenance" count={unassigned.length}>
                  {rows(unassigned)}
                </CollapsibleSection>
              )}

              {inProgress.length > 0 && (
                <CollapsibleSection title="In Progress" count={inProgress.length}>
                  {rows(inProgress)}
                </CollapsibleSection>
              )}

              {complete.length > 0 && (
                <CollapsibleSection title="Complete This Cycle" count={complete.length} defaultOpen={false}>
                  {rows(complete, false)}
                </CollapsibleSection>
              )}
            </>
          )}
        </>
      )}

      {assigningMachine && (
        <MaintenanceAssignSheet
          machineName={assigningMachine.machine_name}
          users={users}
          currentAssignedTo={assigningMachine.assigned_to}
          busy={assigning}
          onAssign={handleAssign}
          onClose={() => setAssigningMachine(null)}
        />
      )}

      {showIntervalEdit && (
        <div className="ml-modal-overlay" onClick={() => setShowIntervalEdit(false)}>
          <div className="ml-modal" onClick={(e) => e.stopPropagation()}>
            <p className="ml-modal__title">Maintenance Interval</p>
            <p className="ml-modal__body">
              Club-wide setting. A machine is "due" when this many days have passed since its last completed record.
            </p>
            <div className="ml-modal__input-row">
              <input
                type="number"
                min="1"
                className="ml-modal__input"
                value={tempInterval}
                onChange={(e) => setTempInterval(e.target.value)}
              />
              <span className="ml-modal__input-suffix">days</span>
            </div>
            <div className="ml-modal__actions">
              <button type="button" className="ml-modal__save" onClick={handleSaveInterval} disabled={savingInterval}>
                Save
              </button>
              <button type="button" className="ml-modal__cancel" onClick={() => setShowIntervalEdit(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <MachineBottomNav />
    </div>
  );
}

export default MachineMaintenanceList;
